import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useMe } from "@/lib/hooks/use-me";
import { getUtcStartOfDay } from "@/utils/time";

/**
 * The open slots this employee may sign up for on a project.
 *
 * It takes two requests because eligibility has two shapes: slots asking for a function you hold,
 * and slots asking for no function at all. The server has no filter that expresses "mine or
 * nobody's", so they are fetched apart and merged here — and merged with a Map, since a slot can
 * satisfy both conditions and would otherwise appear twice in the signup sheet.
 *
 * Lifted out of `routes/app/project/[id]/layout.tsx` when Ontdek needed the same answer to offer
 * signing up from the list. Same reasoning as `lib/signup/date-groups`: this decides what someone
 * can get work on, so there should not be a second copy of it drifting out of step.
 */
export function useProjectSignupSlots(projectId?: string) {
  const { me } = useMe();

  const myFunctions = useQuery({
    queryKey: ["userFunction", me?.id],
    queryFn: () => extractionWrapper(api.userFunction.getMyFunctions()),
    enabled: !!me?.id,
  });

  const myUserFunctions = myFunctions.data;

  const forUserFunction = useQuery({
    queryKey: ["availabilitySlots", "forUserFunction", projectId, myUserFunctions],
    queryFn: () =>
      extractionWrapper(
        api.availabilitySlot.getAll({
          ...openSlotsForProject(projectId),
          filter: {
            ...openSlotsForProject(projectId).filter,
            "userFunction.name": {
              operator: "in",
              values: myUserFunctions?.map((userFunction) => userFunction.name) ?? [],
            },
          },
        }),
      ),
    enabled: !!projectId && !!myUserFunctions?.length,
  });

  const withoutUserFunction = useQuery({
    queryKey: ["availabilitySlots", "withoutUserFunction", projectId],
    queryFn: () =>
      extractionWrapper(
        api.availabilitySlot.getAll({
          ...openSlotsForProject(projectId),
          filter: {
            ...openSlotsForProject(projectId).filter,
            userFunction: {
              operator: "notExists",
            },
          },
        }),
      ),
    enabled: !!projectId,
  });

  const items = useMemo(() => {
    const all = [
      ...(forUserFunction.data?.items ?? []),
      ...(withoutUserFunction.data?.items ?? []),
    ];
    const unique = new Map(all.map((slot) => [slot.id, slot]));

    return Array.from(unique.values()).sort(
      (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
    );
  }, [forUserFunction.data, withoutUserFunction.data]);

  // `myFunctions` has to count, because the function-scoped query stays *disabled* until it lands —
  // and a disabled query reports `isLoading: false`. Reading these two alone would say "ready" while
  // half the slots have not been asked for yet, and the sheet would announce that nothing is open.
  const isLoading = useMemo(
    () => myFunctions.isLoading || forUserFunction.isLoading || withoutUserFunction.isLoading,
    [myFunctions.isLoading, forUserFunction.isLoading, withoutUserFunction.isLoading],
  );

  // A fresh object every render would re-run every memo downstream that depends on it — including
  // `buildSignupDateGroups`, which formats every slot through tempo.
  return useMemo(() => ({ items, isLoading }), [items, isLoading]);
}

/** Everything both requests agree on: this project, published, not in the past, still open. */
function openSlotsForProject(projectId?: string) {
  return {
    filter: {
      "project.id": { operator: "equals" as const, value: projectId },
      status: { operator: "equals" as const, value: "PUBLISHED" as const },
      startAt: { operator: "gte" as const, value: getUtcStartOfDay() },
    },
    include: {
      userFunction: { select: true },
      position: { select: true },
    },
    aggregate: {
      isReserveSlot: { compute: true },
    },
    sort: { field: "startAt" as const, order: "asc" as const },
    onlyShowOpenSlots: true,
    pagination: { page: 1, take: 9999 },
  };
}
